import React from 'react';
import { ImageElement, ImageHistoryEntry } from '../types/storybook';
import { getImageUrl } from '../utils/api';
import './ImageHistoryModal.css';

interface ImageHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  element: ImageElement | null;
  onRestoreVersion?: (elementId: string, url: string) => void;
}

const ACTION_LABELS: Record<string, { icon: string; label: string; color: string }> = {
  created: { icon: '🎨', label: 'Created', color: '#059669' },
  upscaled: { icon: '🔍', label: 'Upscaled', color: '#3B82F6' },
  edited: { icon: '✏️', label: 'Edited', color: '#8B5CF6' },
  cropped: { icon: '✂️', label: 'Cropped', color: '#F59E0B' },
  transparency: { icon: '🔲', label: 'Transparency Applied', color: '#06B6D4' },
  resized: { icon: '↔️', label: 'Resized', color: '#6366F1' },
  replaced: { icon: '🔄', label: 'Replaced', color: '#EC4899' },
};

const ImageHistoryModal: React.FC<ImageHistoryModalProps> = ({
  isOpen,
  onClose,
  element,
  onRestoreVersion,
}) => {
  if (!isOpen || !element) return null;

  const history = element.imageHistory || [];

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionInfo = (action: string) => {
    return ACTION_LABELS[action] || { icon: '📝', label: action, color: '#6B7280' };
  };

  return (
    <div className="image-history-overlay" onClick={onClose}>
      <div className="image-history-content" onClick={(e) => e.stopPropagation()}>
        <div className="image-history-header">
          <h2>📋 Image History</h2>
          <button className="image-history-close" onClick={onClose}>✕</button>
        </div>

        <div className="image-history-body">
          {/* Current Image */}
          <div className="image-history-current">
            <div className="image-history-current-preview">
              <img
                src={getImageUrl(element.url)}
                alt={element.characterName || 'Current Image'}
              />
            </div>
            <div className="image-history-current-info">
              <h3>{element.characterName || 'Image'}</h3>
              {element.naturalWidth && element.naturalHeight && (
                <p className="image-history-resolution">
                  {element.naturalWidth} × {element.naturalHeight}px
                </p>
              )}
              {element.upscaleCount && element.upscaleCount > 0 && (
                <p className="image-history-upscale-count">
                  Upscaled {element.upscaleCount} time{element.upscaleCount > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Timeline */}
          {history.length === 0 ? (
            <div className="image-history-empty">
              <span className="image-history-empty-icon">📭</span>
              <p>No history available for this image yet.</p>
              <p className="image-history-empty-hint">
                Future changes like upscales, edits, and crops will appear here.
              </p>
            </div>
          ) : (
            <div className="image-history-timeline">
              {[...history].reverse().map((entry, idx) => {
                const actionInfo = getActionInfo(entry.action);
                return (
                  <div key={idx} className="image-history-entry">
                    <div className="image-history-entry-line">
                      <div
                        className="image-history-entry-dot"
                        style={{ backgroundColor: actionInfo.color }}
                      />
                      {idx < history.length - 1 && (
                        <div className="image-history-entry-connector" />
                      )}
                    </div>
                    <div className="image-history-entry-content">
                      <div className="image-history-entry-header">
                        <span className="image-history-entry-action" style={{ color: actionInfo.color }}>
                          {actionInfo.icon} {actionInfo.label}
                        </span>
                        <span className="image-history-entry-time">
                          {formatDate(entry.timestamp)}
                        </span>
                      </div>
                      {entry.details && (
                        <p className="image-history-entry-details">{entry.details}</p>
                      )}
                      {entry.resolution && (
                        <p className="image-history-entry-resolution">
                          Resolution: {entry.resolution.width} × {entry.resolution.height}px
                        </p>
                      )}
                      {entry.cost !== undefined && entry.cost > 0 && (
                        <p className="image-history-entry-cost">
                          Cost: {entry.cost} tokens
                        </p>
                      )}
                      {/* Thumbnails for before/after with restore buttons */}
                      <div className="image-history-entry-thumbnails">
                        {entry.previousUrl && (
                          <div className="image-history-thumb-container">
                            <span className="image-history-thumb-label">Before</span>
                            <img
                              src={getImageUrl(entry.previousUrl)}
                              alt="Before"
                              className="image-history-thumb"
                            />
                            {onRestoreVersion && entry.previousUrl !== element.url && (
                              <button
                                className="image-history-restore-btn"
                                onClick={() => {
                                  if (window.confirm('Restore this version? The current image will be replaced.')) {
                                    onRestoreVersion(element.id, entry.previousUrl!);
                                  }
                                }}
                                title="Use this version"
                              >
                                ↩ Use this
                              </button>
                            )}
                            {entry.previousUrl === element.url && (
                              <span className="image-history-current-badge">Current</span>
                            )}
                          </div>
                        )}
                        {entry.newUrl && (
                          <div className="image-history-thumb-container">
                            <span className="image-history-thumb-label">After</span>
                            <img
                              src={getImageUrl(entry.newUrl)}
                              alt="After"
                              className="image-history-thumb"
                            />
                            {onRestoreVersion && entry.newUrl !== element.url && (
                              <button
                                className="image-history-restore-btn"
                                onClick={() => {
                                  if (window.confirm('Restore this version? The current image will be replaced.')) {
                                    onRestoreVersion(element.id, entry.newUrl!);
                                  }
                                }}
                                title="Use this version"
                              >
                                ↩ Use this
                              </button>
                            )}
                            {entry.newUrl === element.url && (
                              <span className="image-history-current-badge">Current</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageHistoryModal;
